<template>
    <div>
        <div slot="title" class="pb-4">
            <div class="flex justify-between items-center">
                <h3 class="text-lg font-medium text-gray-900">
                    {{ showPortalContent ? "Edit Communication Settings" : "Close Ticket & Notify Contacts" }}
                </h3>
                <button :disabled="showPortalContent" v-if="!showPortalContent"
                        class="text-gray-800 font-semibold outline-none hover:text-indigo-600 transition duration-300"
                        @click="closeForm()" type="button">&#10005;
                </button>
            </div>
        </div>
        <!-- Notification Settings -->
        <portal-target name="savedReplyNotificationInputPortal" v-show="showPortalContent" @close="closeForm()"></portal-target>
        <div v-show="!showPortalContent">
            <div v-if="!loading">
                <div v-if="primaryLandlord || primaryTenant" class="flex flex-col space-y-4 py-2">
                    <div class="mb-2 flex flex-wrap items-center justify-between">
                        <div class="font-medium text-md mb-1">Communication Scenario</div>
                        <simple-dropdown :label="selectedCommunicationScenario ? selectedCommunicationScenario.name : 'Choose Scenario' | titlecase"
                                         class="w-full sm:w-1/2">
                            <template slot="buttonOptions">
                                <simple-dropdown-option v-for="scenario in communicationScenarios" class="flex items-center"
                                                        :key="scenario.id + 'scenario'" :value="scenario"
                                                        @click="selectCommunicationScenario(scenario)">
                                    {{ scenario.name | titlecase }}
                                    <icon-chip v-if="scenario.is_system" class="pl-1 text-gray-400 h-2 w-2"></icon-chip>
                                </simple-dropdown-option>
                            </template>
                        </simple-dropdown>
                    </div>

                    <ul class="space-y-4 overflow-y-auto overscroll-none scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-indigo-100 mt-2"
                        v-show="(primaryTenant || primaryLandlord)">
                        <li class="flex flex-col space-y-4 p-4 bg-indigo-50 rounded-md" v-if="primaryTenant">
                            <!-- Tenant Details -->
                            <div class="flex flex-col md:flex-row md:justify-between">
                                <div class="flex flex-col order-2 md:order-1">
                                    <div class="mr-1 font-normal flex items-center">
                                        {{ primaryTenant.full_name }}
                                    </div>

                                    <div class="flex flex-col md:flex-row md:items-center text-indigo-600 text-xs font-normal">
                                        <span v-if="primaryTenant.email" :class="{'text-red-500' : disableTenantEmailOption}"> {{ primaryTenant.email }} </span>
                                        <span v-if="primaryTenant.email && primaryTenant.mobile && notSmallScreen" class="px-2"> &middot;</span>
                                        <span v-if="primaryTenant.mobile" :class="{'text-red-500' : disableTenantSMSOption}"> {{ primaryTenant.mobile }} </span>
                                    </div>
                                </div>

                                <div class="font-medium md:font-normal order-1 md:order-2 flex text-md md:text-sm justify-center mb-1 md:mb-0 md:justify-end">
                                    Primary Tenant
                                </div>
                            </div>

                            <div class="flex flex-col space-y-2">
                                <div v-if="!disableTenantEmailOption">
                                    <saved-reply-notification-input type="email" role="tenant" @input="clearErrors('tenant', 'email')"
                                                                    :to-email="primaryTenant && primaryTenant.email ? primaryTenant.email : null"
                                                                    v-model="form.tenant_email_communication" @toggle-edit-portal="toggleEditPortal()"
                                                                    :populate-for-contactable="{contactable_type: 'App\\Ticket', contactable_id: ticket.id, selectedContact: primaryTenant.id}">
                                    </saved-reply-notification-input>
                                    <span class="text-red-500 text-sm mt-1 flex justify-end"
                                          v-if="form.errors.has(`tenant_email_communication.custom_body`)">
                                        {{ form.errors.get(`tenant_email_communication.custom_body`) }}
                                    </span>
                                    <span class="text-red-500 text-sm mt-1 flex justify-end"
                                          v-if="form.errors.has(`tenant_email_communication.media`)">
                                        {{ form.errors.get(`tenant_email_communication.media`) }}
                                    </span>
                                </div>
                                <div v-else class="font-medium">
                                    {{ state.user.default_mailbox_id === null ? "You do not have a default mailbox" : "Primary tenant has no valid email address" }}
                                </div>

                                <div v-if="!disableTenantSMSOption">
                                    <saved-reply-notification-input type="sms" role="tenant" @input="clearErrors('tenant', 'sms')"
                                                                    v-model="form.tenant_sms_communication" @toggle-edit-portal="toggleEditPortal()"
                                                                    :populate-for-contactable="{contactable_type: 'App\\Ticket', contactable_id: ticket.id, selectedContact: primaryTenant.id}">
                                    </saved-reply-notification-input>
                                    <span class="text-red-500 text-sm mt-1 flex justify-end"
                                          v-if="form.errors.has(`tenant_sms_communication.custom_body`)">
                                        {{ form.errors.get(`tenant_sms_communication.custom_body`) }}
                                    </span>
                                </div>
                                <div v-else class="font-medium">Primary tenant has no valid mobile number</div>
                            </div>
                        </li>

                        <li v-else class="text-md font-medium">There is no primary tenant on this ticket</li>

                        <li class="flex flex-col space-y-2 p-4 bg-indigo-50 rounded-md" v-if="primaryLandlord">
                            <!-- Landlord Details -->
                            <div class="flex flex-col md:flex-row md:justify-between">
                                <div class="flex flex-col order-2 md:order-1">
                                    <div class="mr-1 font-normal flex items-center">
                                        {{ primaryLandlord.full_name }}
                                    </div>

                                    <div class="flex flex-col md:flex-row md:items-center text-indigo-600 text-xs font-normal">
                                        <span v-if="primaryLandlord.email" :class="{'text-red-500' : disableLandlordEmailOption}"> {{ primaryLandlord.email }} </span>
                                        <span v-if="primaryLandlord.email && primaryLandlord.mobile && notSmallScreen" class="px-2"> &middot;</span>
                                        <span v-if="primaryLandlord.mobile" :class="{'text-red-500' : disableLandlordSMSOption}"> {{ primaryLandlord.mobile }} </span>
                                    </div>
                                </div>

                                <div class="font-medium md:font-normal order-1 md:order-2 flex text-md md:text-sm justify-center mb-1 md:mb-0 md:justify-end">
                                    Primary Landlord
                                </div>
                            </div>

                            <div class="flex flex-col space-y-2">
                                <div v-if="!disableLandlordEmailOption">
                                    <saved-reply-notification-input type="email" role="landlord" @input="clearErrors('landlord', 'email')"
                                                                    :to-email="primaryLandlord && primaryLandlord.email ? primaryLandlord.email : null"
                                                                    v-model="form.landlord_email_communication" @toggle-edit-portal="toggleEditPortal()"
                                                                    :populate-for-contactable="{contactable_type: 'App\\Ticket', contactable_id: ticket.id, selectedContact: primaryLandlord.id}">
                                    </saved-reply-notification-input>
                                    <span class="text-red-500 text-sm mt-1 flex justify-end"
                                          v-if="form.errors.has(`landlord_email_communication.custom_body`)">
                                        {{ form.errors.get(`landlord_email_communication.custom_body`) }}
                                    </span>
                                    <span class="text-red-500 text-sm mt-1 flex justify-end"
                                          v-if="form.errors.has(`landlord_email_communication.media`)">
                                        {{ form.errors.get(`landlord_email_communication.media`) }}
                                    </span>
                                </div>
                                <div v-else class="font-medium">
                                    {{ state.user.default_mailbox_id === null ? "You do not have a default mailbox" : "Primary landlord has no valid email address" }}
                                </div>

                                <div v-if="!disableLandlordSMSOption">
                                    <saved-reply-notification-input type="sms" role="landlord" @input="clearErrors('landlord', 'sms')"
                                                                    v-model="form.landlord_sms_communication" @toggle-edit-portal="toggleEditPortal()"
                                                                    :populate-for-contactable="{contactable_type: 'App\\Ticket', contactable_id: ticket.id, selectedContact: primaryLandlord.id}">
                                    </saved-reply-notification-input>
                                    <span class="text-red-500 text-sm mt-1 flex justify-end"
                                          v-if="form.errors.has(`landlord_sms_communication.custom_body`)">
                                        {{ form.errors.get(`landlord_sms_communication.custom_body`) }}
                                    </span>
                                </div>
                                <div v-else class="font-medium">Primary landlord has no valid mobile number</div>
                            </div>
                        </li>

                        <li v-else class="text-md font-medium">There is no primary landlord on this ticket</li>
                    </ul>
                </div>

                <div v-else-if="!primaryTenant && !primaryLandlord" class="flex justify-center my-auto py-24 font-medium">
                    Please assign a primary tenant or landlord to send ticket closed communication
                </div>

                <div v-if="state.user.default_mailbox_id === null">
                    <p class="text-yellow-700 bg-yellow-100 rounded-md p-2 mb-2 break-normal">No default mailbox to send an email from. <a
                        class="text-indigo-400 outline-none" :href="'/users/profile/' + app.userId">Click here</a> to select a default mailbox</p>
                </div>

                <!-- Ticket Close Actions -->
                <div class="flex space-x-2 mt-2 flex-wrap items-center justify-end">
                    <secondary-button :disabled="form.busy"
                                      @click.prevent="submitCloseTicketRequest(false)"
                                      :name="form.busy && form.resolved === false ? 'Closing Ticket...' : 'Close Ticket'">
                    </secondary-button>

                    <secondary-button v-if="ticket.resolved_at === null" :disabled="form.busy"
                                      @click.prevent="submitCloseTicketRequest(true)"
                                      :name="form.busy && form.resolved === true ? 'Closing & Resolving Ticket...' : 'Resolve & Close'">
                    </secondary-button>
                </div>
            </div>

            <div v-else class="flex justify-center my-auto py-24">
                <icon-refresh class="h-10 w-10 text-indigo-600 animate-spin"/>
            </div>
        </div>
    </div>
</template>

<script>
import ContactCommunicationMixin from "../../mixins/contact-communication-mixin";
import CommunicationScenarioMixin from "../../mixins/communication-scenario-mixin";

export default {
    name: "CloseTicketForm",

    mixins: [ContactCommunicationMixin, CommunicationScenarioMixin],

    props: {
        ticket: {
            required: true,
            type: Object,
            description: "The ticket to close"
        }
    },

    data () {
        return {
            form: new AppForm({
                resolved: false,
                tenant_email_communication: {},
                tenant_sms_communication: {},
                landlord_email_communication: {},
                landlord_sms_communication: {}
            }),
            loading: true,
            primaryTenant: null,
            primaryLandlord: null,
            communicationScenarios: [],
            showPortalContent: false,
        };
    },

    computed: {
        disableTenantEmailOption () {
            return this.validateContactEmail(this.primaryTenant);
        },
        disableTenantSMSOption () {
            return this.primaryTenant == null || this.primaryTenant.mobile == null;
        },
        disableLandlordEmailOption () {
            return this.validateContactEmail(this.primaryLandlord);
        },
        disableLandlordSMSOption () {
            return this.primaryLandlord == null || this.primaryLandlord.mobile == null;
        }
    },

    watch: {
        communicationScenarios (newList) {
            if (newList && newList.length > 0) {
                let index = newList.findIndex(scenario => scenario.is_system == true);
                if (index !== -1) {
                    this.selectCommunicationScenario(newList[index]);
                }
            }
        },
    },

    mounted () {
        this.initFormData();
    },

    methods: {
        initFormData () {
            this.loading = true;
            this.getPrimaryLandlord()
            .then(() => this.getPrimaryTenant()
            .then(() => this.getCommunicationScenarios(["closing ticket", "maintenance completed"])
            .then(() => this.loading = false
            ))).catch(() =>
                this.loading = false
            );
        },

        getCommunicationScenarios () {
            return new Promise((resolve, reject) => {
                axios.get("/communication-scenarios/search", {
                    params: {
                        scenario: ["closing ticket", "maintenance completed"]
                    }
                })
                .then(response => {
                    this.communicationScenarios = response.data;
                    resolve();
                })
                .catch(error => {
                    reject();
                    toastr.error(error.message);
                });
            });
        },

        getPrimaryLandlord () {
            return new Promise((resolve, reject) => {
                axios.get("/contacts/primaryLandlord", {
                    params: {
                        contactable: "App\\Ticket",
                        contactable_id: this.ticket.id
                    }
                }).then((response) => {
                    this.primaryLandlord = response.data[0];
                    resolve();
                });
            });
        },

        getPrimaryTenant () {
            return new Promise((resolve, reject) => {
                axios.get("/contacts/primaryTenant", {
                    params: {
                        contactable: "App\\Ticket",
                        contactable_id: this.ticket.id
                    }
                }).then((response) => {
                    this.primaryTenant = response.data[0];
                    resolve();
                });
            });
        },

        selectCommunicationScenario (scenario) {
            this.selectedCommunicationScenario = scenario;

            if (this.selectedCommunicationScenario && this.selectedCommunicationScenario.saved_replies) {
                this.defaultCommunication = this.getSavedReplies(scenario);
                this.setTenantCommunication();
                this.setLandlordCommunication();
            }
        },

        setTenantCommunication () {
            // reset v-model
            this.form.tenant_email_communication = {refresh: true};
            this.form.tenant_sms_communication = {refresh: true};

            this.$nextTick(function () {
                this.form.tenant_email_communication = this.disableTenantEmailOption ? null : _.cloneDeep(this.defaultCommunication.tenant_email);
                this.form.tenant_sms_communication = this.disableTenantSMSOption ? null : _.cloneDeep(this.defaultCommunication.tenant_sms);
            });
        },

        setLandlordCommunication () {
            // reset v-model
            this.form.landlord_email_communication = {refresh: true};
            this.form.landlord_sms_communication = {refresh: true};

            this.$nextTick(function () {
                this.form.landlord_email_communication = this.disableLandlordEmailOption ? null : _.cloneDeep(this.defaultCommunication.landlord_email);
                this.form.landlord_sms_communication = this.disableLandlordSMSOption ? null : _.cloneDeep(this.defaultCommunication.landlord_sms);
            });
        },

        submitCloseTicketRequest (resolvedStatus) {
            this.form.resolved = resolvedStatus;
            App.patch("/tickets/" + this.ticket.id + "/status/close", this.form)
            .then(() => {
                this.$emit("ticket-closed", resolvedStatus);
                this.closeForm();
                Bus.$emit("refresh-activity-list");
                toastr.success("Ticket Closed And Notifications Queued For Sending");
            }).catch((error) => {
                toastr.error("Ticket could not be closed! Error: " + error.message);
            });
        },

        clearErrors (role, commType) {
            if (this.form.errors.has(`${role}_${commType}_communication.custom_body`)) {
                this.form.errors.forget(`${role}_${commType}_communication.custom_body`);
            }
        },

        closeForm () {
            this.$emit("close");
        },

        toggleEditPortal () {
            this.showPortalContent = !this.showPortalContent;
        },
    },
};
</script>

